// src/services/costs/controlExcelExport.js
// Excel-Export der Kostenkontrolle in beiden Ansichten (KOST-015).
// Muster: estimateExcelExport.js (ExcelJS, outlineLevel-Gliederung, CH-Format,
// Kopfzeilen mit Projekt/Phase/Datum/Verfasserin).
import ExcelJS from "exceljs";

const TITLE_FONT = { bold: true, size: 14 };
const LABEL_FONT = { bold: true, size: 10 };
const HEADER_FONT = { bold: true, size: 9 };
const HEADER_FILL = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } };
const HEADER_BORDER = { bottom: { style: "medium", color: { argb: "FF333333" } } };
const LEVEL1_FONT = { bold: true, size: 10 };
const LEVEL1_FILL = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF7F7F7" } };
const LEVEL2_FONT = { bold: true, size: 9 };
const NORMAL_FONT = { size: 9 };
const AGG_FILL = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFAFAFA" } };
const CHF_FMT = `"CHF "#,##0.00`;
const PCT_FMT = `0.0"%"`;
const DANGER_FONT = { size: 9, color: { argb: "FFC62828" } };
const SUCCESS_FONT = { size: 9, color: { argb: "FF2F7D32" } };

/**
 * @param {{
 *   view: 'control'|'kv',
 *   nodes: Array<{node: object, depth: number}>,
 *   totals: Object.<string, object>,
 *   grand: object,
 *   percents: Object.<string, number|null>,
 *   estimateName: string,
 *   linkedProject: {code:string,name:string}|null,
 *   currentPhase: string,
 * }} params
 */
export async function exportCostControlToExcel({
  view,
  nodes,
  totals,
  grand,
  percents,
  estimateName,
  linkedProject,
  currentPhase,
}) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Malheur&Fortuna GmbH";
  workbook.lastModifiedBy = "Malheur&Fortuna GmbH";
  workbook.created = new Date();
  workbook.modified = new Date();

  const ws = workbook.addWorksheet(view === "kv" ? "KV-Original" : "Kostenkontrolle", {
    properties: { outlineLevelRow: 5 },
  });

  // ── Spaltendefinition je Ansicht ────────────────────────────────────────
  const COL = { bkp: 2, name: 3, kv: 4 };
  let columns;
  if (view === "kv") {
    COL.percent = 5;
    columns = [
      [COL.bkp, "BKP-Nr", 12],
      [COL.name, "Bezeichnung", 46],
      [COL.kv, "KV (CHF)", 18],
      [COL.percent, "%/H", 10],
    ];
  } else {
    COL.vertrag = 5;
    COL.erwartung = 6;
    COL.prognose = 7;
    COL.zahlung = 8;
    COL.diff = 9;
    columns = [
      [COL.bkp, "BKP-Nr", 12],
      [COL.name, "Bezeichnung", 40],
      [COL.kv, "KV (CHF)", 16],
      [COL.vertrag, "Vertrag (CHF)", 16],
      [COL.erwartung, "Erwartung (CHF)", 16],
      [COL.prognose, "Prognose (CHF)", 16],
      [COL.zahlung, "Zahlung (CHF)", 16],
      [COL.diff, "Diff (CHF)", 16],
    ];
  }
  const lastCol = columns[columns.length - 1][0];

  ws.getColumn(1).width = 16;
  for (const [col, , width] of columns) {
    ws.getColumn(col).width = width;
  }

  // ── Metadaten ────────────────────────────────────────────────────────────
  const projectLabel = linkedProject
    ? `${linkedProject.code} - ${linkedProject.name}${estimateName ? ` – ${estimateName}` : ""}`
    : estimateName || "";

  ws.getCell("A1").value = view === "kv" ? "KV-Original" : "Kostenkontrolle";
  ws.getCell("A1").font = TITLE_FONT;
  ws.mergeCells(1, 1, 1, lastCol);

  ws.getCell("A2").value = "Projekt";
  ws.getCell("A2").font = LABEL_FONT;
  ws.getCell("B2").value = projectLabel;

  ws.getCell("A3").value = "Phase";
  ws.getCell("A3").font = LABEL_FONT;
  ws.getCell("B3").value = currentPhase || "41 - Ausschreibung";

  ws.getCell("A4").value = "Datum";
  ws.getCell("A4").font = LABEL_FONT;
  ws.getCell("B4").value = new Date().toLocaleDateString("de-CH");

  ws.getCell("A6").value = "Verfasserin";
  ws.getCell("A6").font = LABEL_FONT;
  ws.getCell("B6").value = "Malheur&Fortuna GmbH";
  ws.getCell("B7").value = "Claragraben 103, 4057 Basel";

  // ── Kopfzeile ──────────────────────────────────────────────────────────
  const HEADER_ROW = 9;
  for (const [col, label] of columns) {
    const cell = ws.getCell(HEADER_ROW, col);
    cell.value = label;
    cell.font = HEADER_FONT;
    cell.fill = HEADER_FILL;
    cell.border = HEADER_BORDER;
  }
  ws.views = [{ state: "frozen", ySplit: HEADER_ROW }];

  // ── Datenzeilen ──────────────────────────────────────────────────────────
  // Kommentare belegen eine eigene Folgezeile, deshalb laufende Nummerierung
  // statt DATA_START + Index.
  const DATA_START = HEADER_ROW + 1;
  let nextRow = DATA_START;
  const codeToRow = {};
  nodes.forEach(({ node }) => {
    codeToRow[node.code] = nextRow;
    nextRow += node.comment ? 2 : 1;
  });

  nodes.forEach(({ node }) => {
    const rowNum = codeToRow[node.code];
    const t = totals[node.code] || {};
    const isAggregate = !!t.isAggregate;
    const row = ws.getRow(rowNum);
    row.outlineLevel = Math.max(0, (node.level || 1) - 1);

    const baseFont =
      node.level === 1 ? LEVEL1_FONT : node.level === 2 ? LEVEL2_FONT : NORMAL_FONT;

    const applyBg = (cell) => {
      if (node.level === 1) cell.fill = LEVEL1_FILL;
      else if (isAggregate) cell.fill = AGG_FILL;
    };

    const bkpCell = ws.getCell(rowNum, COL.bkp);
    bkpCell.value = node.code;
    bkpCell.font = baseFont;
    applyBg(bkpCell);

    const nameCell = ws.getCell(rowNum, COL.name);
    nameCell.value = node.name || "";
    nameCell.font = baseFont;
    applyBg(nameCell);

    const money = (col, value) => {
      const c = ws.getCell(rowNum, col);
      c.value = Number(value) || 0;
      c.numFmt = CHF_FMT;
      c.font = baseFont;
      applyBg(c);
      return c;
    };

    money(COL.kv, t.kv);

    if (view === "kv") {
      const pc = ws.getCell(rowNum, COL.percent);
      const pct = percents[node.code];
      if (pct != null) {
        pc.value = pct;
        pc.numFmt = PCT_FMT;
      } else {
        pc.value = "";
      }
      pc.font = baseFont;
      applyBg(pc);
    } else {
      money(COL.vertrag, t.vertrag);
      money(COL.erwartung, t.erwartung);
      money(COL.prognose, t.prognose);
      money(COL.zahlung, t.zahlung);
      const diffCell = money(COL.diff, t.diff);
      // Diff einfaerben: positiv (Mehrkosten) rot, negativ (Einsparung) gruen
      if ((t.diff || 0) > 0.005) diffCell.font = DANGER_FONT;
      else if ((t.diff || 0) < -0.005) diffCell.font = SUCCESS_FONT;
    }

    // ── Kommentarzeile: kursiv, nur in der Bezeichnungs-Spalte, sonst leer ──
    if (node.comment) {
      const commentRowNum = rowNum + 1;
      ws.getRow(commentRowNum).outlineLevel = Math.max(0, (node.level || 1) - 1);
      const commentCell = ws.getCell(commentRowNum, COL.name);
      commentCell.value = node.comment;
      commentCell.font = { size: 9, italic: true };
      commentCell.alignment = { wrapText: true, vertical: "top" };
    }
  });

  // ── Total-Zeile ──────────────────────────────────────────────────────────
  const TOTAL_ROW = nextRow + 1;
  const labelCell = ws.getCell(TOTAL_ROW, COL.name);
  labelCell.value = "Total";
  labelCell.font = { bold: true, size: 11 };
  labelCell.alignment = { horizontal: "right" };

  const totalMoney = (col, value, extraFont) => {
    const c = ws.getCell(TOTAL_ROW, col);
    c.value = Number(value) || 0;
    c.numFmt = CHF_FMT;
    c.font = extraFont || { bold: true, size: 11 };
    c.border = { top: { style: "medium", color: { argb: "FF333333" } } };
  };

  totalMoney(COL.kv, grand.kv);
  if (view === "control") {
    totalMoney(COL.vertrag, grand.vertrag);
    totalMoney(COL.erwartung, grand.erwartung);
    totalMoney(COL.prognose, grand.prognose);
    totalMoney(COL.zahlung, grand.zahlung);
    const diffFont =
      (grand.diff || 0) > 0.005
        ? { bold: true, size: 11, color: { argb: "FFC62828" } }
        : (grand.diff || 0) < -0.005
          ? { bold: true, size: 11, color: { argb: "FF2F7D32" } }
          : { bold: true, size: 11 };
    totalMoney(COL.diff, grand.diff, diffFont);
  }

  // ── Seiteneinrichtung ─────────────────────────────────────────────────────
  ws.pageSetup.orientation = "landscape";
  ws.pageSetup.fitToPage = true;
  ws.pageSetup.fitToWidth = 1;
  ws.pageSetup.fitToHeight = 0;

  // ── Download ───────────────────────────────────────────────────────────
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;

  const now = new Date();
  const dateStr =
    String(now.getFullYear()).slice(2) +
    String(now.getMonth() + 1).padStart(2, "0") +
    String(now.getDate()).padStart(2, "0");
  const projectCode = linkedProject?.code || "";
  const viewTag = view === "kv" ? "KV" : "KK";
  const safeName = (estimateName || "Kostenkontrolle").replace(/[^a-zA-Z0-9äöüÄÖÜ_-]/g, "_");
  a.download = `${projectCode ? projectCode + "_" : ""}${viewTag}_41_${dateStr}_${safeName}.xlsx`;
  a.click();
  window.URL.revokeObjectURL(url);
}
